import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "default-roles" }
const _hoisted_2 = { class: "default-roles-card" }
const _hoisted_3 = { class: "roles-content" }
const _hoisted_4 = { class: "default-roles-header" }
const _hoisted_5 = { class: "default-roles-title" }
const _hoisted_6 = { class: "roles-actions" }
const _hoisted_7 = {
  key: 0,
  class: "roles-table"
}
const _hoisted_8 = { class: "roles-list-header" }
const _hoisted_9 = { class: "header" }
const _hoisted_10 = { class: "name" }
const _hoisted_11 = { class: "hourlyRate" }
const _hoisted_12 = {
  key: 1,
  class: "loading apollo"
}
const _hoisted_13 = {
  key: 2,
  class: "empty"
}
const _hoisted_14 = { class: "delete-list" }
const _hoisted_15 = {
  key: 0,
  class: "loading apollo"
}

import { computed, ref, provide } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { useQuery, useResult, useMutation } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import EntitiesList from "@/components/list/EntitiesList.vue";
import ButtonIcon from "@/components/ButtonIcon.vue";
import Modal from "@/components/Modal.vue";
import BasicButton from "@/components/BasicButton.vue";

import AddRoles from "@/components/role/AddRoles.vue";
import EditRoles from "@/components/role/EditRoles.vue";

import { GET_DEFAULT_ROLES } from "@/graphql/defaultRole/queries";
import { DELETE_DEFAULT_ROLES } from "@/graphql/defaultRole/mutations";

import { formatPrice } from "@/helpers/utils";
import enUK from "@/locales/en-UK.json";

interface DefaultRole {
  id: string;
  name: string;
  hourlyRate: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminRoles',
  setup(__props) {

const { t } = useI18n({
  useScope: "global",
  inheritLocale: true,
  messages: {
    "en-UK": enUK,
  },
});

const toast = useToast();

const {
  result: rolesResult,
  loading: rolesLoading,
  onError: onRolesError,
  refetch: refetchRoles,
} = useQuery(GET_DEFAULT_ROLES);

onRolesError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.getDefaultRoles"));
});

const defaultRoles = useResult(
  rolesResult,
  [],
  (data) => data?.defaultRoles?.data ?? [],
);

const isAddModalVisible = ref(false);
const isEditModalVisible = ref(false);
const isDeleteModalVisible = ref(false);

const selectedRole = ref<DefaultRole | null>(null);
const checkBoxes = ref<boolean[]>([]);

const selectedRoles = computed(() => {
  const selected: DefaultRole[] = [];

  checkBoxes.value.forEach((checked, index) => {
    if (checked && defaultRoles.value[index]) {
      selected.push(defaultRoles.value[index]);
    }
  });

  return selected;
});

const formatHourlyRate = (rate: number): string => {
  return formatPrice(rate);
};

const {
  mutate: deleteRoles,
  onError: onDeleteRoleError,
  onDone: onDeleteRoleDone,
  loading: deletingRole,
} = useMutation(DELETE_DEFAULT_ROLES);

onDeleteRoleError((error) => {
  logErrorMessages(error);
  toast.error(t("errors.deleteDefaultRoles"));
});

onDeleteRoleDone(() => {
  toast.success(t("success.deleteDefaultRoles"));
  closeDeleteModal();
  refetchRoles();
  checkBoxes.value = [];
});

function showAddModal(): void {
  isAddModalVisible.value = true;
}

function closeAddModal(): void {
  isAddModalVisible.value = false;
}

function showEditModal(role: DefaultRole): void {
  selectedRole.value = role;
  isEditModalVisible.value = true;
}

function closeEditModal(): void {
  isEditModalVisible.value = false;
  selectedRole.value = null;
}

function showDeleteModal(): void {
  if (selectedRoles.value.length > 0) {
    isDeleteModalVisible.value = true;
  }
}

function closeDeleteModal(): void {
  isDeleteModalVisible.value = false;
}

const formatters = {
  hourlyRate: formatHourlyRate,
};

const columnsOrder = {
  name: null,
  hourlyRate: null,
};

provide("columnsOrder", columnsOrder);

async function handleDeleteRoles(): Promise<void> {
  const ids = selectedRoles.value.map((role) => role.id);

  await deleteRoles({ ids });
}

function handleRoleClick(role: DefaultRole): void {
  showEditModal(role);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("h2", _hoisted_5, _toDisplayString(_unref(t)("titles.roles")), 1),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(ButtonIcon, {
              label: _unref(t)('buttons.addRoles'),
              "icon-name": "material-symbols:add",
              onClick: showAddModal
            }, null, 8, ["label"]),
            (selectedRoles.value.length > 0)
              ? (_openBlock(), _createBlock(ButtonIcon, {
                  key: 0,
                  label: _unref(t)('buttons.deleteRoles'),
                  "icon-name": "material-symbols:delete",
                  onClick: showDeleteModal
                }, null, 8, ["label"]))
              : _createCommentVNode("", true)
          ])
        ]),
        (_unref(defaultRoles).length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("ul", _hoisted_8, [
                _createElementVNode("li", _hoisted_9, [
                  _cache[1] || (_cache[1] = _createElementVNode("div", { class: "checkbox-placeholder" }, null, -1)),
                  _createElementVNode("div", _hoisted_10, _toDisplayString(_unref(t)("labels.name")), 1),
                  _createElementVNode("div", _hoisted_11, _toDisplayString(_unref(t)("labels.hourlyRate")), 1)
                ])
              ]),
              _createVNode(EntitiesList, {
                elements: _unref(defaultRoles),
                "has-check-box": true,
                formatters: formatters,
                checkBoxes: checkBoxes.value,
                "onUpdate:checkBoxes": _cache[0] || (_cache[0] = ($event: any) => ((checkBoxes).value = $event)),
                onGoEntityDetails: handleRoleClick
              }, null, 8, ["elements", "checkBoxes"])
            ]))
          : (_unref(rolesLoading))
            ? (_openBlock(), _createElementBlock("div", _hoisted_12, _toDisplayString(_unref(t)("loading")), 1))
            : (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(_unref(t)("empty.defaultRoles")), 1))
      ])
    ]),
    (isAddModalVisible.value)
      ? (_openBlock(), _createBlock(AddRoles, {
          key: 0,
          onClose: closeAddModal
        }))
      : _createCommentVNode("", true),
    (isEditModalVisible.value && selectedRole.value)
      ? (_openBlock(), _createBlock(EditRoles, {
          key: 1,
          role: selectedRole.value,
          roles: _unref(defaultRoles),
          onClose: closeEditModal
        }, null, 8, ["role", "roles"]))
      : _createCommentVNode("", true),
    (isDeleteModalVisible.value)
      ? (_openBlock(), _createBlock(Modal, {
          key: 2,
          onClose: closeDeleteModal
        }, {
          header: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("titles.role.delete")), 1)
          ]),
          body: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(_unref(t)("messages.deleteRoleConfirmation", {
              count: selectedRoles.value.length,
            })), 1),
            _createElementVNode("ul", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(selectedRoles.value, (role) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: role.id
                }, _toDisplayString(role.name), 1))
              }), 128))
            ]),
            (_unref(deletingRole))
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, _toDisplayString(_unref(t)("loading")), 1))
              : _createCommentVNode("", true)
          ]),
          footer: _withCtx(() => [
            _createVNode(BasicButton, {
              class: "delete-button",
              type: "submit",
              disabled: _unref(deletingRole),
              onClick: handleDeleteRoles
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.delete")), 1)
              ]),
              _: 1
            }, 8, ["disabled"]),
            _createVNode(BasicButton, {
              class: "cancel-button",
              onClick: closeDeleteModal
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)("buttons.cancel")), 1)
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})